/**
 * Yetki (grant) kodlarının tek kaynağı (012, FR-016). Kodlar kanonik olarak DB'de tanımlıdır (analist
 * yönetir) — bu modül yalnız **kodda referans verilen** kodları tek merkezde toplar; dağınık string
 * literal'leri ve yazım hatası riskini önler (typo derlemede yakalanır, otomatik tamamlama gelir).
 * Backend karşılığı {@code com.rdc.ceosuite.auth.Grants}'tır; isimler birebir aynı tutulur.
 *
 * Kullanım: `hasGrant(GRANTS.ACTIVITY_VIEW)`, `<Can grant={GRANTS.USER_MANAGE}>`, `viewGrants: [GRANTS.MINUTES_VIEW]`.
 */
export const GRANTS = {
  USER_MANAGE: 'USER_MANAGE',
  ROLE_MANAGE: 'ROLE_MANAGE',
  GRANT_MANAGE: 'GRANT_MANAGE',
  SYSTEM_PARAMETER_MANAGE: 'SYSTEM_PARAMETER_MANAGE',
  REFERANS_MANAGE: 'REFERANS_MANAGE',
  COUNTRY_MANAGE: 'COUNTRY_MANAGE',
  CRITERIA_SCORE_RANGE_MANAGE: 'CRITERIA_SCORE_RANGE_MANAGE',
  /** Tutanak oluşturma (038 interim): eski MINUTES_VIEW yerine — "Tutanak Oluştur" menüsü. */
  MINUTES_CREATE: 'MINUTES_CREATE',
  /** Alt aksiyon durum güncelleme (066): atananı olmasa da alt aksiyon statüsünü değiştirebilir (Özel Kalem/Super Admin). */
  SUBACTION_STATUS_UPDATE: 'SUBACTION_STATUS_UPDATE',
  /** Ana/bağımsız aksiyon durum güncelleme (066): atananı olmasa da aksiyon statüsünü değiştirebilir (Özel Kalem/Super Admin). */
  ACTION_STATUS_UPDATE: 'ACTION_STATUS_UPDATE',
  /** Aksiyon silme — tümü (067; 092'de Süper Admin'e daraltıldı): atananı/oluşturanı olmasa da herhangi bir aksiyonu siler. */
  ACTION_DELETE_ANY: 'ACTION_DELETE_ANY',
  /** Aksiyon silme — kendi oluşturduğu (092): oluşturma yetkilerinden bağımsız, silmeye özel yetki. İstemci bunu doğrudan kontrol etmez — sunucudan gelen `deletable` alanına güvenir. */
  ACTION_DELETE_OWN: 'ACTION_DELETE_OWN',
  /**
   * **Kayıt silme** (2026-09-11) — görev · alt görev · gündem maddesi · prensip kararı için TEK yetki.
   * Silme her yerde soft-delete'tir ve denetim izi yazar. Roller: Süper Admin · Özel Kalem Personeli ·
   * Özel Kalem Müdürü · Genel Müdür. `ACTION_DELETE_ANY`/`ACTION_DELETE_OWN` bu yetkiyle **değişmedi**:
   * onlar 2026-08-25'te katalogdan kalkmıştı ve diriltilmedi (sahiplik ölçütü geri gelmedi).
   */
  RECORD_DELETE: 'RECORD_DELETE',
  /** Toplantı Kayıt Tipi (032) yönetimi — kendi dedike yetkisi. */
  MEETING_DECISION_TYPE_MANAGE: 'MEETING_DECISION_TYPE_MANAGE',
  /** Toplantı grubu görüntüleme (038): menü + liste/detay okuma. */
  MEETING_GROUP_VIEW: 'MEETING_GROUP_VIEW',
  /** Toplantı grubu yönetimi (038): ekle/düzenle/durum/sil. */
  MEETING_GROUP_MANAGE: 'MEETING_GROUP_MANAGE',
  /** Toplantı Türü parametre ekranı (22.09.2026) — menü + rota + uçların tek kapısı. */
  MEETING_TYPE_MANAGE: 'MEETING_TYPE_MANAGE',
  /** Tatil Günü parametre ekranı (23.09.2026) — menü + rota + uçların tek kapısı. */
  HOLIDAY_DAY_MANAGE: 'HOLIDAY_DAY_MANAGE',
  /** Liste Yönetimi (114) — Admin → Parametreler; adlandırılmış kişi listelerinin tek yetkisi. */
  MANAGER_LIST_MANAGE: 'MANAGER_LIST_MANAGE',
  /**
   * Stratejik Hedefler / Katkılar parametre ekranları (127) — menü + rota + yönetim uçlarının kapısı
   * (roller 1 · 3 · 4). Görev formlarındaki seçenekler bu yetkiye bağlı DEĞİL (görev okuma yetkisiyle
   * `/actions/levels`).
   */
  STRATEGIC_OBJECTIVE_MANAGE: 'STRATEGIC_OBJECTIVE_MANAGE',
  CONTRIBUTION_MANAGE: 'CONTRIBUTION_MANAGE',
  /** Zaman Portfoyu Veri Girisi (iki sekme: Toplanti Kaydi + Ucus Kaydi) — tek yetki. */
  TIME_PORTFOLIO_MANAGE: 'TIME_PORTFOLIO_MANAGE',
  /**
   * Zaman Portfoyu RAPORU goruntuleme (28.09.2026) — veri girisinden AYRI kod.
   * Genel Mudur raporu gorur ama veri girmez; GM Asistani tersine yalniz veri girer.
   */
  TIME_PORTFOLIO_REPORT_VIEW: 'TIME_PORTFOLIO_REPORT_VIEW',
  /**
   * Notlarım (113) — menü + rota kilidi. **Tüm rollere** verilir: modül TK Seyir'e erişimi
   * olan herkese açıktır. Yetkisiz menü öğesi emsali olmadığı ve rota kilidi yetki haritasını
   * menüden türettiği için, "herkes görsün" bu şekilde ifade edilir (spec 113 → research D6).
   *
   * <p>Erişim kararı bu kodla **verilmez**: kullanıcı yalnız kendi listelerini ve kendisiyle
   * paylaşılanları görür, yazma yalnız liste sahibine açıktır — ikisi de **kayıt düzeyinde**,
   * sunucuda uygulanır.
   */
  TODO_LIST_VIEW: 'TODO_LIST_VIEW',
  /**
   * Ajanda (125, yetki 15.10.2026) — menü, takvim ve davet oluşturma/düzenleme/silme. **Davetli bu
   * yetkiden bağımsızdır:** davet edildiği toplantının detayını açıp yanıt verir (rota kilitsizdir).
   */
  AGENDA_VIEW: 'AGENDA_VIEW',
  /**
   * Pano önceliklendirme (2026-10-02) — "Aksiyon Beklenen Maddeler" tablosundaki "Önceliklendir" ve
   * "Görevleri Önceliklendir" butonları + sunucudaki üç hatırlatma ucu. Roller: Genel Müdür
   * Yardımcısı ve Başkan (6) **hariç** tümü. Önceden rol kimliğine sabit bağlıydı.
   */
  DASHBOARD_PRIORITIZE: 'DASHBOARD_PRIORITIZE',
  /**
   * "Toplantı Yönetimi" ekranını görme (2026-09-16). Ekran Görevler menüsünün altındadır ve
   * içeriği henüz tasarlanmamıştır; bu kod bugün **yalnız** menü görünürlüğünü ve rota kilidini
   * (menuGuard) sürer. Roller: Süper Admin (1) · Özel Kalem Personeli (3) · Özel Kalem Müdürü (4).
   * Ekran veri göstermeye başladığında uçları da bu kodla korunmalıdır — aksi halde kilit yalnız
   * istemcide kalır (asıl koruma backend'dedir, FR-009).
   */
  MEETING_MANAGEMENT_VIEW: 'MEETING_MANAGEMENT_VIEW',
  /** İşlem Kodu (045): salt liste + TR/EN ad güncelle (ekleme/silme/durum yok). */
  OPERATION_CODE_MANAGE: 'OPERATION_CODE_MANAGE',
  /** Ön Ek Tanımları (056): salt liste + ad/ön ek güncelle (ekleme/silme/durum yok). */
  PREFIX_MANAGE: 'PREFIX_MANAGE',
  /** Bildirim Şablonu (059): tam CRUD (REF_NOTIFICATION_TEMPLATE). */
  NOTIFICATION_TEMPLATE_MANAGE: 'NOTIFICATION_TEMPLATE_MANAGE',
  /** Şablon Parametreleri (059): tam CRUD; parametre + grup sekmelerinin ikisini de kapsar. */
  TEMPLATE_PARAMETER_MANAGE: 'TEMPLATE_PARAMETER_MANAGE',
  /**
   * Vekalet (090): `USERS_PROXY` ekranının **tüm** işlemleri — liste okuma, ekleme, "Vekaleti Kaldır" ve
   * kişi seçicisinin araması — bu **tek** anahtara bağlıdır (kullanıcı kararı 2026-08-19; `*_VIEW` /
   * `*_MANAGE` ayrımı yok). Backend `Grants.USER_PROXY_MANAGE` ile birebir. Menü ve rota da bunu okur;
   * asıl koruma sunucudadır.
   */
  USER_PROXY_MANAGE: 'USER_PROXY_MANAGE',
  /**
   * Anasayfa Yönetici Raporu — Genel Müdür kapsamı (068). Backend {@code Grants.DASHBOARD_GM_SCOPE} ile
   * birebir. Kişi **kapsamı** sunucuda çözülür; istemci bu kodu yalnız **hangi panonun açılacağını**
   * seçmek için okur — {@link DASHBOARD_YKB_SCOPE} ile birlikte, sunucunun `managerReport` kuralıyla
   * aynı (2026-10-13; gerekçe `features/home/types.ts` → `viewFor`).
   */
  DASHBOARD_GM_SCOPE: 'DASHBOARD_GM_SCOPE',
  /**
   * Anasayfa Yönetici Raporu — Yönetim Kurulu Başkanı kapsamı (2026-10-12). `DASHBOARD_GM_SCOPE`'un
   * ikizi: aynı panoyu açar, kişi kümesini YKB'ye doğrudan bağlı kişilerden kurar. İstemci bunu da
   * yalnız pano seçimi için okur ({@link DASHBOARD_GM_SCOPE} ile birlikte — birine bakmak, ötekiyle
   * gelen kullanıcıyı Raporum'a düşürür).
   */
  DASHBOARD_YKB_SCOPE: 'DASHBOARD_YKB_SCOPE',

  // ── 075 Yetki granülerliği: Toplantı/Aksiyon ekran & eylem yetkileri (Kitap1.xlsx; DB'de seed'li).
  //    Backend com.rdc.ceosuite.auth.Grants ile birebir. Gating sonraki fazlarda (bkz. specs/075/plan.md).
  MINUTES_PUBLISH: 'MINUTES_PUBLISH',
  MEETING_REPORT_VIEW: 'MEETING_REPORT_VIEW',
  MEETING_HISTORY_VIEW: 'MEETING_HISTORY_VIEW',
  MEETING_FILE_MANAGE: 'MEETING_FILE_MANAGE',
  MEETING_FILE_DOWNLOAD: 'MEETING_FILE_DOWNLOAD',
  MEETING_ARCHIVE_VIEW: 'MEETING_ARCHIVE_VIEW',
  MINUTE_ITEM_IMPORT: 'MINUTE_ITEM_IMPORT',
  PRINCIPLE_DECISION_VIEW: 'PRINCIPLE_DECISION_VIEW',
  PRINCIPLE_DECISION_MANAGE: 'PRINCIPLE_DECISION_MANAGE',
  ACTION_MENU_VIEW: 'ACTION_MENU_VIEW',
  ACTION_LIST_ALL_VIEW: 'ACTION_LIST_ALL_VIEW',
  /**
   * "Tüm Görevler"in **sınırlı** hâli (06.10.2026, Genel Müdür): Başkan ve üzerinin kendi ekipleri için
   * açtığı doğrudan görevler hariç her şey. Daraltmayı sunucu yapar; istemci için sekmeyi açan ikinci
   * yetkidir → {@link ACTION_LIST_ALL_SCOPE_GRANTS}.
   */
  ACTION_LIST_ALL_LIMITED_VIEW: 'ACTION_LIST_ALL_LIMITED_VIEW',
  ACTION_LIST_OWN_VIEW: 'ACTION_LIST_OWN_VIEW',
  ACTION_LIST_ASSIGNED_VIEW: 'ACTION_LIST_ASSIGNED_VIEW',
  ACTION_LIST_FOLLOWED_VIEW: 'ACTION_LIST_FOLLOWED_VIEW',
  ACTION_DETAIL_VIEW: 'ACTION_DETAIL_VIEW',
  /** **Yeni/bağımsız** görev açma. Alt görev ekleme AYRI koddur → `SUBACTION_CREATE`. */
  ACTION_CREATE: 'ACTION_CREATE',
  ACTION_MANAGE: 'ACTION_MANAGE',
  /**
   * Var olan bir görevin **altına alt görev ekleme** (2026-08-30). `ACTION_CREATE`'ten ayrıdır:
   * alt görev güncelleme/silme zaten `ACTION_MANAGE` iken ekleme `ACTION_CREATE` istiyordu ve
   * `ACTION_MANAGE` taşıyıp `ACTION_CREATE` taşımayan bir rolde (Yönetici) Ana Sorumlu alt görevi
   * silebiliyor ama ekleyemiyordu. Ucu `ACTION_MANAGE`'e çekmek 089 vekalet yasağını delerdi —
   * gerekçenin tamamı backend `Grants#SUBACTION_CREATE` javadoc'unda.
   */
  SUBACTION_CREATE: 'SUBACTION_CREATE',
  /** Aksiyon notu düzenle/sil (herhangi bir notu; silme soft-delete). Not ekleme ACTION_MANAGE ile. */
  ACTION_NOTE_MANAGE: 'ACTION_NOTE_MANAGE',
  ACTION_ARCHIVE_VIEW: 'ACTION_ARCHIVE_VIEW',
  SUBACTION_PAGE_VIEW: 'SUBACTION_PAGE_VIEW',
  SUBACTION_ASSIGNEE_UPDATE: 'SUBACTION_ASSIGNEE_UPDATE',

  /**
   * Termin güncelleme talebi (087) — **ayrıcalıklı** onay/ret. Bu yetkiye sahip kullanıcı, kök aksiyonu
   * oluşturan kişi olmasa da her talebi işleyebilir ve listede **tümünü** görür. Sahiplik yolu ayrıdır ve
   * tek başına yeterlidir (kök aksiyonu oluşturan bu yetki olmadan da kendi taleplerini işler).
   *
   * Kod DB kataloğuna 2026-08-14'te eklendi (`specs/087-.../db/087_deadline_request_approve_grant_seed.sql`);
   * **başlangıçta** Super Admin · Genel Müdür · Özel Kalem Müdürü · Özel Kalem Personeli rollerinde
   * seed'liydi. **Bugün yalnız Super Admin'de:** Özel Kalem rolleri 2026-08-14'te Rol Yönetimi
   * ekranından, **Genel Müdür 2026-08-22'de kullanıcı kararıyla**
   * (`specs/087-.../db/087b_deadline_request_approve_gm_revoke.sql`) geri alındı — GM'ye tüm talepler
   * düşüyordu, oysa yalnız **kendi oluşturduğu** aksiyonların talepleri düşmelidir (o da sahiplik
   * koluyla, ayrıcalık gerektirmeden sağlanır). Rol bağı **veridir** (012: DB kanonik, analist yönetir);
   * buradaki liste bilgilendirmedir, koda gömülü kural değildir. Yetkisi olmayan kullanıcıda ekran
   * **sahiplik koluyla** çalışmaya devam eder (hata değil, beklenen davranış).
   */
  DEADLINE_UPDATE_REQUEST_APPROVE: 'DEADLINE_UPDATE_REQUEST_APPROVE',

  /**
   * Hedef teslim tarihi güncelleme talebini **her görevde** girebilme ayrıcalığı (kullanıcı kuralı
   * 2026-08-28: Özel Kalem Müdürü + Özel Kalem Personeli). Bu yetkisi olmayan kullanıcı yalnız
   * **kendisine atanan** görevde talep girer: kök görevde **Ana Sorumlu** olmalıdır (Yan Sorumlu
   * giremez), alt görevde o alt göreve **eklenmiş** olmalıdır (ana görevin terminine giremez).
   *
   * **İstemci bunu OKUMAZ** — buton görünürlüğü sunucudan tek bir bayrakla gelir
   * (`ActionDetailDto.deadlineRequestAllowed`). Kod burada yalnız katalog aynası olarak durur
   * (`Grants.java` ile birebir); istemcide yeniden hesaplanırsa 066'daki ekran↔uç ayrışması geri döner.
   * Rol bağı **veridir** (012): `specs/087-termin-guncelleme-talebi/db/087c_deadline_request_any_grant_seed.sql`.
   */
  DEADLINE_UPDATE_REQUEST_ANY: 'DEADLINE_UPDATE_REQUEST_ANY',

  /**
   * Mevcut bir görevin hedef teslim tarihini **doğrudan** değiştirme hakkı (130, 2026-11-02): tarih
   * değiştirilebilir ⇔ bu yetki ∨ görevi oluşturan.
   *
   * **İstemci bunu OKUMAZ** — alanın açıklığı sunucudan gelir (`ActionDetail.deadlineEditable`,
   * `MeetingAction.deadlineEditable`); "oluşturan mıyım" istemcide ölçülemez. Kod burada yalnız katalog
   * aynasıdır (`Grants.java` ile birebir). Rol bağı veridir:
   * `specs/130-hedef-teslim-tarihi-yetkisi/db/130a_action_deadline_update_grant.sql`.
   */
  ACTION_DEADLINE_UPDATE: 'ACTION_DEADLINE_UPDATE',

  /**
   * Görevler › **Onaylarım** ekranının kapısı (131, 2026-11-03): menü öğesi ve rota kilidi. Ekranın
   * sekmeleri bu koda DEĞİL kendi işlemlerinin mevcut yetkisine bakar (talepler →
   * `ACTION_READ_ANY_GRANTS`, görev kapatma → `MEETING_MANAGEMENT_VIEW`). Rol bağı veridir:
   * `specs/131-onay-bekleyenler/db/131a_my_approvals_view_grant.sql`.
   */
  MY_APPROVALS_VIEW: 'MY_APPROVALS_VIEW',

  /** Admin → Bildirimler (078) — SEND_ITEM gönderim kayıtlarının salt-okunur izlenmesi (menü + liste + detay). */
  SENT_EMAIL_VIEW: 'SENT_EMAIL_VIEW',
  /**
   * Admin → Bildirimler (078-b) — "Deneme sayısını sıfırla" satır aksiyonu: kaydı dış geçidin
   * (Notifigate) kuyruğuna geri koyar. `SENT_EMAIL_VIEW`'den **ayrıdır**: izlemek ile yeniden gönderim
   * tetiklemek aynı güven düzeyi değildir (yetkisi olmayanda buton hiç render edilmez).
   */
  SENT_EMAIL_MANAGE: 'SENT_EMAIL_MANAGE',

  /**
   * Kayıt-No kolonlarının görünürlüğü (077) — Aksiyon No / Prensip Karar No / Toplantı No / Grup No / madde No
   * gibi tüm `recordNumber` tablo kolonları **tek** bu yetkiye bağlıdır (tablo tablo ayrılmaz). Yalnız Özel Kalem
   * Müdürü + Özel Kalem Personeli rollerinde seed'lidir; yetkisi olmayan kullanıcıda bu "No" kolonları gizlenir.
   * Saf frontend kolon-görünürlüğü — `recordNumber` DTO'larda dönmeye devam eder (backend gate yok).
   */
  RECORD_NUMBER_VIEW: 'RECORD_NUMBER_VIEW',

  ACTIVITY_VIEW: 'ACTIVITY_VIEW',
  ACTIVITY_CREATE: 'ACTIVITY_CREATE',
  ACTIVITY_STAKEHOLDER_MANAGE: 'ACTIVITY_STAKEHOLDER_MANAGE',
  ACTIVITY_EVENT_TYPE_MANAGE: 'ACTIVITY_EVENT_TYPE_MANAGE',
  ACTIVITY_INVITATION_TYPE_MANAGE: 'ACTIVITY_INVITATION_TYPE_MANAGE',
  ACTIVITY_CRITERIA_SCORE_MANAGE: 'ACTIVITY_CRITERIA_SCORE_MANAGE',
  ACTIVITY_PARTICIPATION_STATUS_VIEW: 'ACTIVITY_PARTICIPATION_STATUS_VIEW',
  ACTIVITY_PARTICIPATION_STATUS_UPDATE: 'ACTIVITY_PARTICIPATION_STATUS_UPDATE',
  ACTIVITY_EXPORT: 'ACTIVITY_EXPORT',

  TICKET_REQUEST_VIEW: 'TICKET_REQUEST_VIEW',
  TICKET_REQUEST_VIEW_OWN: 'TICKET_REQUEST_VIEW_OWN',
  /** Özel kalem personeli (037): kendi + tam PNR/Ad-Soyad ile başkalarının talepleri. */
  TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER: 'TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER',
  TICKET_REQUEST_CREATE: 'TICKET_REQUEST_CREATE',
  TICKET_REQUEST_UPDATE: 'TICKET_REQUEST_UPDATE',
  TICKET_REQUEST_APPROVE: 'TICKET_REQUEST_APPROVE',
  TICKET_REQUEST_EXPORT: 'TICKET_REQUEST_EXPORT',
  TICKET_REQUEST_TYPE_MANAGE: 'TICKET_REQUEST_TYPE_MANAGE',
  TICKET_REJECT_REASON_MANAGE: 'TICKET_REJECT_REASON_MANAGE',

  // ── Yönetim ekranlarının SALT OKUNUR türevleri (126 FR-019, 26.10.2026) ──
  // DB'de YOKTUR, hiçbir role atanamaz: yalnız Kısıtlı Yetkili asistan personasında, asil karşılık
  // gelen `_MANAGE` kodunu taşıyorsa sunucu türetir (`ProxyGrantPolicy.READONLY_SHADOWED`). Menü ve
  // rota kilidi `viewGrants`'ta MANAGE ile birlikte bunları da kabul eder; ekran salt okunur kipi
  // `useProxyMode().readOnly`'den okur, yazma uçlarını sunucu yalnız MANAGE ile açar.
  USER_MANAGE_READONLY: 'USER_MANAGE_READONLY',
  ROLE_MANAGE_READONLY: 'ROLE_MANAGE_READONLY',
  GRANT_MANAGE_READONLY: 'GRANT_MANAGE_READONLY',
  SYSTEM_PARAMETER_MANAGE_READONLY: 'SYSTEM_PARAMETER_MANAGE_READONLY',
  REFERANS_MANAGE_READONLY: 'REFERANS_MANAGE_READONLY',
  OPERATION_CODE_MANAGE_READONLY: 'OPERATION_CODE_MANAGE_READONLY',
  NOTIFICATION_TEMPLATE_MANAGE_READONLY: 'NOTIFICATION_TEMPLATE_MANAGE_READONLY',
  TEMPLATE_PARAMETER_MANAGE_READONLY: 'TEMPLATE_PARAMETER_MANAGE_READONLY',
  MEETING_GROUP_MANAGE_READONLY: 'MEETING_GROUP_MANAGE_READONLY',
  MEETING_DECISION_TYPE_MANAGE_READONLY: 'MEETING_DECISION_TYPE_MANAGE_READONLY',
  PREFIX_MANAGE_READONLY: 'PREFIX_MANAGE_READONLY',
  MEETING_TYPE_MANAGE_READONLY: 'MEETING_TYPE_MANAGE_READONLY',
  HOLIDAY_DAY_MANAGE_READONLY: 'HOLIDAY_DAY_MANAGE_READONLY',
  MANAGER_LIST_MANAGE_READONLY: 'MANAGER_LIST_MANAGE_READONLY',
  STRATEGIC_OBJECTIVE_MANAGE_READONLY: 'STRATEGIC_OBJECTIVE_MANAGE_READONLY',
  CONTRIBUTION_MANAGE_READONLY: 'CONTRIBUTION_MANAGE_READONLY',
} as const

/** Geçerli grant kodu (union). `hasGrant`/`Can` bu türü bekler → literal typo derlemede yakalanır. */
export type GrantCode = (typeof GRANTS)[keyof typeof GRANTS]

/**
 * **Alt görev ekleyebilme** — backend `Grants#HAS_SUBACTION_CREATE` bileşiğinin aynası
 * (`ACTION_CREATE` VEYA `SUBACTION_CREATE`).
 *
 * OR bilinçlidir: bağımsız görev açabilen biri var olan bir görevin altına da iş kırabilir; tersi
 * doğru değildir. Yalnız `SUBACTION_CREATE`'e bakılsaydı bugün `ACTION_CREATE` ile ekleyebilen roller
 * (Özel Kalem, Genel Müdür, Super Admin) bu yeteneği kaybederdi. **Backend ile birlikte değiştirilir** —
 * biri güncellenip diğeri unutulursa buton görünür ama uç 403 döner (aynı tuzak `ACTION_READ_ANY_GRANTS`
 * javadoc'unda da yazılı).
 *
 * ⚠ **İNVARYANT — `SUBACTION_CREATE` TEK BAŞINA YETMEZ, `ACTION_MANAGE` ile BİRLİKTE verilir.**
 * Butonun gerçek koşulu `editable && hasAnyGrant(...)`; `editable` ise `ACTION_MANAGE` + sunucunun
 * kayıt-düzeyi iznini içerir (`canManage`, aşağıdaki `ActionDetailPage`). Yani istemci koşulu uçtan
 * **daha dardır**: uç `ACTION_MANAGE` istemez. `SUBACTION_CREATE` verilip `ACTION_MANAGE` verilmeyen
 * bir rolde uç 201 döner ama **buton hiç çizilmez** — 2026-08-30'da düzeltilen hatanın birebir
 * tekrarı. Bugün böyle bir rol yok (yeni kodu alan tek rol Yönetici ve onda `ACTION_MANAGE` var);
 * yeni bir role verilirken bu çift kontrol edilmelidir. Push kapısı code review'ünde yakalandı.
 */
export const SUBACTION_CREATE_GRANTS = [
  GRANTS.ACTION_CREATE,
  GRANTS.SUBACTION_CREATE,
] as const satisfies readonly [GrantCode, ...GrantCode[]]

/**
 * Backend `Grants.HAS_ACTION_READ_ANY` composite'inin **frontend aynası**: aksiyon menüsü/listesi/detayı/
 * arşivi/alt-aksiyon sayfasından **herhangi birini** görebilme yetkisi.
 *
 * Aksiyon domenine ait ekranların menü görünürlüğü ve rota koruması bu tek listeden okunur; ekran başına
 * kopya dizi yazılmaz. **Backend ile birlikte değiştirilir** — biri güncellenip diğeri unutulursa menü
 * görünür ama uç 403 döner (087 `/speckit-analyze` bulgusu I2).
 */
export const ACTION_READ_ANY_GRANTS = [
  GRANTS.ACTION_MENU_VIEW,
  GRANTS.ACTION_LIST_ALL_VIEW,
  GRANTS.ACTION_LIST_ALL_LIMITED_VIEW,
  GRANTS.ACTION_LIST_OWN_VIEW,
  GRANTS.ACTION_LIST_ASSIGNED_VIEW,
  GRANTS.ACTION_LIST_FOLLOWED_VIEW,
  GRANTS.ACTION_DETAIL_VIEW,
  GRANTS.ACTION_ARCHIVE_VIEW,
  GRANTS.SUBACTION_PAGE_VIEW,
] as const satisfies readonly [GrantCode, ...GrantCode[]]

/**
 * **"Tüm Görevler"** sekmesini açan yetkiler — backend `ActionQueryService.requireScopeGrant`'in ALL
 * kolunun aynası. Menü, rota, kök yönlendirme ve pano bağlantıları bu tek listeden okur; biri güncellenip
 * diğeri unutulursa sınırlı yetkili kullanıcı (Genel Müdür) menüyü görür ama rotada "yetkiniz yok"a düşer.
 */
export const ACTION_LIST_ALL_SCOPE_GRANTS = [
  GRANTS.ACTION_LIST_ALL_VIEW,
  GRANTS.ACTION_LIST_ALL_LIMITED_VIEW,
] as const satisfies readonly [GrantCode, ...GrantCode[]]
